import React, {
    useState,
} from 'react'


import useTitle from "@/hooks/useTitle"
import useAuth from '@/hooks/useAuth'


/*
|--------------------------------------------------------------------------
| Existing Trail Components
|--------------------------------------------------------------------------
*/

import EtUserTrailSessionsListProdComp
    from '@/features/userTrailSessions/EtUserTrailSessionsListProdComp'

import EtSponsorUserPadListCompProd
    from '@/features/sponsorUserPads/EtSponsorUserPadListCompProd'

import EtCoachUserPadListCompProd
    from '@/features/coachUserPads/EtCoachUserPadListCompProd'

import EtOrgInitiationBoard
    from '@/features/orgIntentionBoards/EtOrgInitiationBoard'

import EtCoachOrgInitiations
    from '@/features/orgIntentionBoards/EtCoachOrgInitiations'

// 27 Sep 2026
import EtUserInitiationWorkspace
  from "@/features/orgIntentionBoards/EtUserInitiationWorkspace"

const EtUserInduction = ({
    userId,
    userOrgId,
}) => {

    // First Created - 19 Oct 2024
    // Author - Atul Pandey
    //
    // Updated - 20 Oct 2024 - added user trail session list comp
    // Updated - 21 Oct 2024 - added sponsor user pad list comp
    // Updated - 23 Feb 2025 - changed user session listing to prod comp
    // Updated - 03 Mar 2025 - changed sponsor and coach user pad comp to prod
    // Updated - 09 May 2025 - moved User Packs and Trails to user profile
    // Updated - 22 Aug 2025 - inserted Et Interpret Scores
    // Updated - 03 Mar 2026 - muted Et Approach section
    //
    // Updated - 24 Sep 2026
    // Restructured Induction around role based journey views:
    //
    // Me As a User
    // Me As an Org Sponsor
    // Me As an Org Facilitator
    // Me As an Et Coach
    //
    // Existing Trail / Session components remain unchanged.
    //
    // Updated - 27 Sep 2026 - My Initiation


    /*
    |--------------------------------------------------------------------------
    | Page Title
    |--------------------------------------------------------------------------
    */

    useTitle(
        'User Induction'
    )


    /*
    |--------------------------------------------------------------------------
    | Authentication / Roles
    |--------------------------------------------------------------------------
    */

    const {

        isSponsor,

        isEtCoach,

        isOrgFacilitator,

    } =
        useAuth()


    /*
    |--------------------------------------------------------------------------
    | Primary Role Navigation
    |--------------------------------------------------------------------------
    |
    | All Trinity users get the User view.
    |
    | Additional role views are exposed based on the logged in user's roles.
    |
    */

    const [
        activeInductionRole,
        setActiveInductionRole,
    ] =
        useState(
            'user'
        )


    /*
    |--------------------------------------------------------------------------
    | User View Navigation
    |--------------------------------------------------------------------------
    */

    const [
        activeUserView,
        setActiveUserView,
    ] =
        useState(
            'completeJourney'
        )


    /*
    |--------------------------------------------------------------------------
    | Sponsor View Navigation
    |--------------------------------------------------------------------------
    */

    const [
        activeSponsorView,
        setActiveSponsorView,
    ] =
        useState(
            'orgInitiation'
        )


    /*
    |--------------------------------------------------------------------------
    | Facilitator View Navigation
    |--------------------------------------------------------------------------
    */

    const [
        activeFacilitatorView,
        setActiveFacilitatorView,
    ] =
        useState(
            'orgInitiation'
        )


    /*
    |--------------------------------------------------------------------------
    | Et Coach View Navigation
    |--------------------------------------------------------------------------
    */

    const [
        activeCoachView,
        setActiveCoachView,
    ] =
        useState(
            'orgInitiations'
        )



    /*
    |--------------------------------------------------------------------------
    | Return
    |--------------------------------------------------------------------------
    */

    return (

        <div className="font-poppins">


            {/* 
            |--------------------------------------------------------------------------
            | Section 1 - Induction Role Navigation
            |--------------------------------------------------------------------------
            */}

            <InductionRoleNavigation

                activeInductionRole={
                    activeInductionRole
                }

                setActiveInductionRole={
                    setActiveInductionRole
                }

                isSponsor={
                    isSponsor
                }

                isOrgFacilitator={
                    isOrgFacilitator
                }

                isEtCoach={
                    isEtCoach
                }

            />



            {/* 
            |--------------------------------------------------------------------------
            | Section 2 - Role Content
            |--------------------------------------------------------------------------
            */}

            <div className="m-1">


                {/* 
                |--------------------------------------------------------------------------
                | Me As a User
                |--------------------------------------------------------------------------
                */}

                {
                    activeInductionRole ===
                    'user'
                    &&
                    (
                        <UserInductionView

                            userId={
                                userId
                            }

                            activeView={
                                activeUserView
                            }

                            setActiveView={
                                setActiveUserView
                            }

                        />
                    )
                }



                {/* 
                |--------------------------------------------------------------------------
                | Me As an Org Sponsor
                |--------------------------------------------------------------------------
                */}

                {
                    activeInductionRole ===
                    'sponsor'
                    &&
                    isSponsor
                    &&
                    (
                        <SponsorInductionView

                            userId={
                                userId
                            }

                            userOrgId={
                                userOrgId
                            }

                            activeView={
                                activeSponsorView
                            }

                            setActiveView={
                                setActiveSponsorView
                            }

                        />
                    )
                }



                {/* 
                |--------------------------------------------------------------------------
                | Me As an Org Facilitator
                |--------------------------------------------------------------------------
                */}

                {
                    activeInductionRole ===
                    'facilitator'
                    &&
                    isOrgFacilitator
                    &&
                    (
                        <FacilitatorInductionView

                            userId={
                                userId
                            }

                            userOrgId={
                                userOrgId
                            }

                            activeView={
                                activeFacilitatorView
                            }

                            setActiveView={
                                setActiveFacilitatorView
                            }

                        />
                    )
                }



                {/* 
                |--------------------------------------------------------------------------
                | Me As an Et Coach
                |--------------------------------------------------------------------------
                */}

                {
                    activeInductionRole ===
                    'coach'
                    &&
                    isEtCoach
                    &&
                    (
                        <CoachInductionView

                            userId={
                                userId
                            }

                            userOrgId={
                                userOrgId
                            }

                            activeView={
                                activeCoachView
                            }

                            setActiveView={
                                setActiveCoachView
                            }

                        />
                    )
                }


            </div>


        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Induction Role Navigation
|--------------------------------------------------------------------------
|
| Top level navigation.
|
| This answers:
|
| "Which role / hat am I wearing?"
|
*/

const InductionRoleNavigation = ({

    activeInductionRole,

    setActiveInductionRole,

    isSponsor,

    isOrgFacilitator,

    isEtCoach,

}) => {


    return (

        <div className="m-1 p-1 bg-gray-100 rounded">


            <div
                className="
                    flex
                    justify-start
                    items-center
                    flex-wrap
                    gap-2
                    m-1
                    p-1
                "
            >


                {/* 
                |--------------------------------------------------------------------------
                | User
                |--------------------------------------------------------------------------
                */}

                <InductionRoleTab

                    label="Me As a User"

                    isActive={
                        activeInductionRole ===
                        'user'
                    }

                    onClick={
                        () =>
                            setActiveInductionRole(
                                'user'
                            )
                    }

                />



                {/* 
                |--------------------------------------------------------------------------
                | Org Sponsor
                |--------------------------------------------------------------------------
                */}

                {
                    isSponsor
                    &&
                    (
                        <InductionRoleTab

                            label="Me As an Org Sponsor"

                            isActive={
                                activeInductionRole ===
                                'sponsor'
                            }

                            onClick={
                                () =>
                                    setActiveInductionRole(
                                        'sponsor'
                                    )
                            }

                        />
                    )
                }



                {/* 
                |--------------------------------------------------------------------------
                | Org Facilitator
                |--------------------------------------------------------------------------
                */}

                {
                    isOrgFacilitator
                    &&
                    (
                        <InductionRoleTab

                            label="Me As an Org Facilitator"

                            isActive={
                                activeInductionRole ===
                                'facilitator'
                            }

                            onClick={
                                () =>
                                    setActiveInductionRole(
                                        'facilitator'
                                    )
                            }

                        />
                    )
                }



                {/* 
                |--------------------------------------------------------------------------
                | Et Coach
                |--------------------------------------------------------------------------
                */}

                {
                    isEtCoach
                    &&
                    (
                        <InductionRoleTab

                            label="Me As an Et Coach"

                            isActive={
                                activeInductionRole ===
                                'coach'
                            }

                            onClick={
                                () =>
                                    setActiveInductionRole(
                                        'coach'
                                    )
                            }

                        />
                    )
                }


            </div>


        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Individual Role Tab
|--------------------------------------------------------------------------
*/

const InductionRoleTab = ({

    label,

    isActive,

    onClick,

}) => {


    return (

        <button

            type="button"

            onClick={
                onClick
            }

            className={`
                px-4
                py-2
                rounded
                text-xs
                font-bold
                transition
                cursor-pointer

                ${
                    isActive
                        ?
                        `
                        bg-orange-600
                        text-white
                        shadow
                        `
                        :
                        `
                        bg-gray-200
                        text-gray-600
                        hover:bg-gray-300
                        `
                }
            `}
        >

            {
                label
            }

        </button>

    )

}



/*
|--------------------------------------------------------------------------
| User Induction View
|--------------------------------------------------------------------------
|
| Phase 1
|
| My Complete Journey
| My Initiation
| My Trails
|
| User journey and initiation are placeholders for now.
|
*/

const UserInductionView = ({

    userId,

    activeView,

    setActiveView,

}) => {


    const tabs = [

        {
            key:
                'completeJourney',

            label:
                'My Complete Journey',
        },

        {
            key:
                'initiation',

            label:
                'My Initiation',
        },

        {
            key:
                'trails',

            label:
                'My Trails',
        },

    ]


    return (

        <div>


            <InductionSubNavigation

                tabs={
                    tabs
                }

                activeView={
                    activeView
                }

                setActiveView={
                    setActiveView
                }

            />



            {/* 
            |--------------------------------------------------------------------------
            | Complete Journey
            |--------------------------------------------------------------------------
            */}

            {
                activeView ===
                'completeJourney'
                &&
                (
                    <InductionPlaceholder

                        title="My Complete Journey"

                        description="Your organization's Intention Boards, candidate initiation journeys and onboarding progress will appear here."

                    />
                )
            }



            {/* 
            |--------------------------------------------------------------------------
            | My Initiation
            |--------------------------------------------------------------------------
            */}

            {
                activeView ===
                'initiation'
                &&
                (
                    <EtUserInitiationWorkspace />
                )
            }



            {/* 
            |--------------------------------------------------------------------------
            | My Trails
            |--------------------------------------------------------------------------
            */}

            {
                activeView ===
                'trails'
                &&
                (
                    <div className="mt-2">

                        <EtUserTrailSessionsListProdComp

                            userId={
                                userId
                            }

                        />

                    </div>
                )
            }


        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Sponsor Induction View
|--------------------------------------------------------------------------
|
| My Org's Initiation
| My Org's Trails
|
*/

const SponsorInductionView = ({

    userId,

    userOrgId,

    activeView,

    setActiveView,

}) => {


    const tabs = [

        {
            key:
                'orgInitiation',

            label:
                "My Org's Initiation",
        },

        {
            key:
                'orgTrails',

            label:
                "My Org's Trails",
        },

    ]


    return (

        <div>


            <InductionSubNavigation

                tabs={
                    tabs
                }

                activeView={
                    activeView
                }

                setActiveView={
                    setActiveView
                }

            />



            {/* 
            |--------------------------------------------------------------------------
            | Org Initiation
            |--------------------------------------------------------------------------
            */}

            {
                activeView ===
                'orgInitiation'
                &&
                (
                    <EtOrgInitiationBoard
                        userOrgId={userOrgId}
                    />
                )
            }



            {/* 
            |--------------------------------------------------------------------------
            | Org Trails
            |--------------------------------------------------------------------------
            */}

            {
                activeView ===
                'orgTrails'
                &&
                (
                    <div className="mt-2">

                        <EtSponsorUserPadListCompProd

                            sponsorId={
                                userId
                            }

                            sponsorOrgId={
                                userOrgId
                            }

                        />

                    </div>
                )
            }


        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Org Facilitator Induction View
|--------------------------------------------------------------------------
|
| Phase 1:
|
| My Org's Initiation
|
| Further facilitator views can be added later.
|
*/

const FacilitatorInductionView = ({

    userId,

    userOrgId,

    activeView,

    setActiveView,

}) => {


    const tabs = [

        {
            key:
                'orgInitiation',

            label:
                "My Org's Initiation",
        },

    ]


    return (

        <div>


            <InductionSubNavigation

                tabs={
                    tabs
                }

                activeView={
                    activeView
                }

                setActiveView={
                    setActiveView
                }

            />



            {
                activeView ===
                'orgInitiation'
                &&
                (
                    <EtOrgInitiationBoard
                        userOrgId={userOrgId}
                    />
                )
            }


        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Et Coach Induction View
|--------------------------------------------------------------------------
|
| My Coached Orgs' Initiations
| My Coached Org Trails
|
*/

const CoachInductionView = ({

    userId,

    userOrgId,

    activeView,

    setActiveView,

}) => {


    const tabs = [

        {
            key:
                'orgInitiations',

            label:
                "My Coached Orgs' Initiations",
        },

        {
            key:
                'orgTrails',

            label:
                "My Coached Org Trails",
        },

    ]


    return (

        <div>


            <InductionSubNavigation

                tabs={
                    tabs
                }

                activeView={
                    activeView
                }

                setActiveView={
                    setActiveView
                }

            />



            {/* 
            |--------------------------------------------------------------------------
            | Coached Org Initiations
            |--------------------------------------------------------------------------
            */}

            {
                activeView ===
                'orgInitiations'
                &&
                (
                    <div className="mt-2">

                        <EtCoachOrgInitiations

                            coachUserId={
                                userId
                            }

                        />

                    </div>
                )
            }



            {/* 
            |--------------------------------------------------------------------------
            | Coached Org Trails
            |--------------------------------------------------------------------------
            */}

            {
                activeView ===
                'orgTrails'
                &&
                (
                    <div className="mt-2">

                        <EtCoachUserPadListCompProd

                            coachUserId={
                                userId
                            }

                            coachOrgId={
                                userOrgId
                            }

                        />

                    </div>
                )
            }


        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Shared Sub Navigation
|--------------------------------------------------------------------------
|
| Secondary navigation within the selected role.
|
| Examples:
|
| Sponsor
|   My Org's Initiation
|   My Org's Trails
|
| Coach
|   My Coached Orgs' Initiations
|   My Coached Org Trails
|
*/

const InductionSubNavigation = ({

    tabs = [],

    activeView,

    setActiveView,

}) => {


    return (

        <div className="bg-gray-200 rounded m-1 p-1">


            <div
                className="
                    flex
                    justify-start
                    items-center
                    flex-wrap
                    gap-1
                "
            >


                {
                    tabs.map(
                        (
                            tab
                        ) => {

                            const isActive =
                                activeView ===
                                tab.key


                            return (

                                <button

                                    key={
                                        tab.key
                                    }

                                    type="button"

                                    onClick={
                                        () =>
                                            setActiveView(
                                                tab.key
                                            )
                                    }

                                    className={`
                                        px-3
                                        py-1.5
                                        text-[11px]
                                        font-bold
                                        rounded
                                        cursor-pointer
                                        transition

                                        ${
                                            isActive
                                                ?
                                                `
                                                bg-gray-50
                                                text-orange-600
                                                shadow-sm
                                                `
                                                :
                                                `
                                                text-gray-600
                                                hover:bg-gray-100
                                                `
                                        }
                                    `}
                                >

                                    {
                                        tab.label
                                    }

                                </button>

                            )

                        }
                    )
                }


            </div>


        </div>

    )

}



/*
|--------------------------------------------------------------------------
| Temporary Placeholder
|--------------------------------------------------------------------------
|
| Used only while the new Initiation components are being built.
|
*/

const InductionPlaceholder = ({

    title,

    description,

}) => {


    return (

        <div
            className="
                m-1
                mt-2
                p-5
                bg-gray-100
                rounded
            "
        >


            <p
                className="
                    text-sm
                    font-bold
                    text-gray-700
                "
            >

                {
                    title
                }

            </p>


            <p
                className="
                    mt-1
                    text-xs
                    italic
                    text-gray-500
                    max-w-3xl
                "
            >

                {
                    description
                }

            </p>


        </div>

    )

}



export default EtUserInduction